import React, { useEffect, useState } from 'react';
import classes from "./PredictionGame2.module.css";
import { FaArrowLeft, FaCircle, FaMinus } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from "moment-timezone";
import { getPredictionLeaderboardApi, getUserPredictionsApi, matchDetailsApi } from '../../http/http';
import { useDispatch, useSelector } from 'react-redux';
import MatchEvents2 from '../../component/NewComponents/MatchEvents2';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';
import { toast } from 'react-toastify';

const PredictionGame2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [matchDetails, setMatchDetails] = useState(null);
    const [matchDetails2, setMatchDetails2] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const { userDetails } = useSelector(state => state?.persisted?.userSlice);
    const [time, setTime] = useState(null);
    const [display, setDisplay] = useState("block");
    const [tab, setTab] = useState("mypredictions");

    const [loading, setLoading] = useState(true);
    const [loader, setLoader] = useState(true);

    const [leaderboard, setLeaderboard] = useState([]);
    const [userRank, setUserRank] = useState(null);

    const dispatch = useDispatch();

    const fetchMatchDetails = async () => {
        try {
            const response = await matchDetailsApi(location?.state?.match_fixture_id, userDetails?.token);
            setMatchDetails(response?.data);
            if (response?.data?.match_details) setMatchDetails2(JSON.parse(response?.data?.match_details));
        } catch (error) {
            if (error?.response?.status == 403) {
                dispatch(removeUserDetails());
                toast.error("Login Again!");
                navigate('/');
            } else {
                toast.error(error?.response?.data?.message || error?.response?.message || error?.data?.message || "Something Went Wrong!");
            }
        }
    };

    const fetchUserPredictions = async () => {
        try {
            const response = await getUserPredictionsApi(location?.state?.match_fixture_id, userDetails?.token);
            setPredictions(JSON.parse(response?.data?.[0].predictions));
        } catch (error) { console.log(error, 'error---'); }
    };

    const fetchLeaderboard = async () => {
        try {
            const response = await getPredictionLeaderboardApi(location?.state?.match_fixture_id, userDetails?.token);
            setLeaderboard(response?.data?.leaderboard);
            setUserRank(response?.data?.userRank?.[0]);
        } catch (error) { console.log(error, 'error---'); }
    };

    const fetchData = async (load) => {
        if (load) setLoading(true);
        setLoader(true);
        fetchMatchDetails(load);
        fetchUserPredictions(load);
        fetchLeaderboard(load);
        setLoading(false);
        setLoader(false);
    };

    useEffect(() => {
        if (location?.state?.match_fixture_id) fetchData(true);
        else navigate("/home");
    }, [location?.state?.match_fixture_id]);

    useEffect(() => {
        if (!loader) {
            const intervalId = setInterval(() => {
                if (!document.hidden) fetchData(false);
            }, 10000);
            return () => clearInterval(intervalId);
        }
    }, [loader]);

    const matchTimer = (match_date_time) => {
        try {
            const liveTime = moment();
            const matchTime = moment.utc(match_date_time);
            const timeLeft = moment.duration(matchTime.diff(liveTime));
            if (timeLeft.asMilliseconds() <= 0) setDisplay("none");
            const hours = Math.floor(timeLeft.asHours());
            const minutes = timeLeft.minutes();
            const seconds = timeLeft.seconds();
            setTime(`${hours}h ${minutes}m ${seconds}s`);
        } catch (error) { console.log(error, 'error--'); }
    };

    useEffect(() => {
        if (matchDetails?.match_status == "NS" && display != "none") {
            const intervalId = setInterval(() => matchTimer(matchDetails?.match_date_time), 1000);
            return () => clearInterval(intervalId);
        }
    }, [matchDetails, display]);

    const renderStatus = () => {
        const s = matchDetails?.match_status;
        const elapsed = matchDetails2?.fixture?.status?.elapsed;
        const extra = matchDetails2?.fixture?.status?.extra;
        if (["FT","AET","PEN"].includes(s)) return <span className={classes.status_text}>Full Time</span>;
        if (s === "SUSP") return <span className={classes.status_text}>Suspended</span>;
        if (s === "PST")  return <span className={classes.status_text}>Postponed</span>;
        if (s === "CANC") return <span className={classes.status_text}>Cancelled</span>;
        if (["ABD","AWD","WO"].includes(s)) return <span className={classes.status_text}>Abandoned</span>;
        if (s === "HT")   return <span className={classes.status_text}><FaCircle className={`${classes.text_danger_glow} ${classes.blink}`} style={{ fontSize: 8, marginRight: 6 }} />Half-Time {elapsed}`</span>;
        if (s === "ET")   return <span className={classes.status_text}><FaCircle className={`${classes.text_danger_glow} ${classes.blink}`} style={{ fontSize: 8, marginRight: 6 }} />{elapsed}` ET +{extra}`</span>;
        if (s === "P")    return <span className={classes.status_text}><FaCircle className={`${classes.text_danger_glow} ${classes.blink}`} style={{ fontSize: 8, marginRight: 6 }} />Penalties {elapsed}`</span>;
        if (["1H","2H","LIVE"].includes(s)) return <span className={classes.status_text}><FaCircle className={`${classes.text_danger_glow} ${classes.blink}`} style={{ fontSize: 8, marginRight: 6 }} />{elapsed}`</span>;
        return null;
    };

    // Render a single prediction card — handles multi-value (4,7,8) and single-value cases
    const renderPrediction = (prediction, index) => {
        const [key, value] = Object.entries(prediction)[0];
        const isMulti = ["prediction4","prediction7","prediction8"].includes(key);

        const question   = isMulti ? value?.[0]?.question : value?.question;
        const answer1    = isMulti ? value?.[0]?.answer : value?.answer;
        const answer2    = isMulti ? value?.[1]?.answer : null;
        const hasResult  = isMulti ? value?.[0]?.message !== "" : value?.message !== "";
        const message    = isMulti ? value?.[0]?.message : value?.message;
        const points     = isMulti ? (value?.[0]?.point ?? 0) + (value?.[1]?.point ?? 0) : value?.point;

        return (
            <div className={classes.question} key={index} style={{ animationDelay: `${index * 0.05}s` }}>
                <p className={classes.question_title}>{question}</p>
                <div className={classes.answer_container}>
                    <p className={classes.answer}>{answer1}</p>
                </div>
                {answer2 != null && (
                    <div className={classes.answer_container}>
                        <p className={classes.answer}>{answer2}</p>
                    </div>
                )}
                {hasResult && (
                    <div className={classes.message_container}>
                        <p className={classes.message}>{message}</p>
                        <div className={classes.points_container}>
                            <p className={classes.point}>+{points} pts</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={classes.page}>
            <div className={classes.pageInner}>

                {loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80dvh' }}>
                        <SmallLoader />
                    </div>
                ) : (
                    <>
                        {/* Sticky header */}
                        <div className={classes.header}>
                            <div className={classes.header_nav}>
                                <div className={classes.header_nav_container}>
                                    <button type="button" className={classes.back_button} onClick={() => navigate("/home")}>
                                        <FaArrowLeft className={classes.back_icon} />
                                    </button>
                                </div>
                            </div>

                            {/* Match strip */}
                            <div className={classes.match_details_container}>
                                {renderStatus() && <div className={classes.match_status}>{renderStatus()}</div>}

                                <div className={classes.match_details}>
                                    <div className={classes.team}>
                                        <img src={matchDetails?.home_team_image} alt={matchDetails?.home_team_name} className={classes.team_logo} />
                                        <span className={classes.team_name}>{matchDetails?.home_team_name}</span>
                                    </div>
                                    <div className={classes.score_container}>
                                        <span className={classes.score}>{matchDetails2?.goals?.home}</span>
                                        <div className={classes.separator}><FaMinus className={classes.separator_icon} /></div>
                                        <span className={classes.score}>{matchDetails2?.goals?.away}</span>
                                    </div>
                                    <div className={classes.team}>
                                        <img src={matchDetails?.away_team_image} alt={matchDetails?.away_team_name} className={classes.team_logo} />
                                        <span className={classes.team_name}>{matchDetails?.away_team_name}</span>
                                    </div>
                                </div>

                                {matchDetails?.match_status == "NS" && (
                                    <div className={classes.match_timer_container} style={{ display }}>
                                        <span className={classes.match_timer}>⏱ {time}</span>
                                    </div>
                                )}

                                <span className={classes.match_time}>
                                    {moment.utc(matchDetails?.match_date_time).local().format("dddd, DD MMM YYYY [at] hh:mm A")}
                                </span>
                            </div>

                            {/* Tabs */}
                            <div className={classes.tabs_container}>
                                {[
                                    { key: "mypredictions", label: "My Predictions" },
                                    { key: "leaderboard",   label: "Leaderboard" },
                                    { key: "events",        label: "Events" },
                                ].map(t => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`${classes.tab_option} ${tab === t.key ? classes.active : ''}`}
                                        onClick={() => setTab(t.key)}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tab content */}
                        <div className={classes.content}>
                            {tab === "mypredictions" && (
                                <>
                                    {predictions.length > 0 && predictions.map((prediction, index) =>
                                        renderPrediction(prediction, index)
                                    )}
                                    {matchDetails?.match_status == "NS" && (
                                        <div className={classes.edit_button_container}>
                                            <button
                                                type="button"
                                                className={classes.edit_button}
                                                onClick={() => navigate("/create-prediction", {
                                                    state: { match_fixture_id: matchDetails?.match_fixture_id, edit: true }
                                                })}
                                            >
                                                EDIT PREDICTIONS
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {tab === "events" && (
                                <div className={classes.grid_col_2}>
                                    <MatchEvents2
                                        team_name={matchDetails?.home_team_name}
                                        data={matchDetails2?.events?.filter(d =>
                                            d?.team?.name == matchDetails?.home_team_name &&
                                            d?.type == "Goal" &&
                                            d?.detail != "Missed Penalty"
                                        )}
                                    />
                                    <MatchEvents2
                                        team_name={matchDetails?.away_team_name}
                                        data={matchDetails2?.events?.filter(d =>
                                            d?.team?.name == matchDetails?.away_team_name &&
                                            d?.type == "Goal" &&
                                            d?.detail != "Missed Penalty"
                                        )}
                                    />
                                </div>
                            )}

                            {tab === "leaderboard" && (
                                <div className={classes.leaderboard_container}>
                                    <div className={classes.headings}>
                                        <div className={classes.rank_heading}>Rank</div>
                                        <div className={classes.number_heading}>Number</div>
                                        <div className={classes.points_heading}>Points</div>
                                    </div>
                                    {leaderboard?.map((data, index) => (
                                        <div className={classes.leaderboard_data} key={index}>
                                            <div className={classes.rank_data}>{data?.rank}</div>
                                            <div className={classes.number_data}>{data?.msisdn}</div>
                                            <div className={classes.points_data}>{data?.total_points}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PredictionGame2;